import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { linkClasses } from "@/constants/ui";
import { getInitials } from "@/lib/utils";
import { Link } from "react-router";

interface WelcomeBackStepProps {
  firstName?: string;
  avatar?: string;
  email?: string;
  password: string;
  onPasswordChange: (value: string) => void;
  onSubmit: () => void;
  onNotMe: () => void;
  pending: boolean;
  error?: string;
}

const WelcomeBackStep = ({ firstName, avatar, email, password, onPasswordChange, onSubmit, onNotMe, pending, error }: WelcomeBackStepProps) => {
  const [show, setShow] = useState(false);

  // Derive avatar fallback: name initials if we have a name, else first letter of email, else "?"
  const avatarFallback = firstName
    ? getInitials(firstName)
    : email
      ? email[0].toUpperCase()
      : "?";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-2">
        <Avatar className="w-16 h-16">
          {avatar && <AvatarImage src={avatar} alt={firstName ?? email ?? "User"} />}
          <AvatarFallback>{avatarFallback}</AvatarFallback>
        </Avatar>
        <h3 className="text-xl font-semibold">
          {firstName ? `Welcome back, ${firstName}` : "Welcome back"}
        </h3>
      </div>
      {error && (
        <div className="p-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800" role="alert">{error}</div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
        <FieldSet>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="password">Password</FieldLabel>
              <div className="relative">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  className="bg-white dark:text-gray-900 text-lg pr-10"
                  value={password}
                  onChange={(e) => onPasswordChange(e.target.value)}
                />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute inset-y-0 right-2 flex items-center text-gray-500" aria-label={show ? "Hide password" : "Show password"}>
                  {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </Field>
            <Field orientation="horizontal">
              <Button type="submit" className="w-full" disabled={!password || pending} aria-label="Login">Login</Button>
            </Field>
          </FieldGroup>
        </FieldSet>
      </form>
      <div className="flex justify-between mt-2">
        <Link to="/forgot-password" className={linkClasses}>Forgotten password?</Link>
        <button type="button" onClick={onNotMe} className={linkClasses}>
          {firstName ? `I'm not ${firstName}` : "Use a different email"}
        </button>
      </div>
    </div>
  );
};

export default WelcomeBackStep;
